import { useState } from 'react';
import { IconImageOutline } from '@picsart/design-system/foundation/icons/IconImageOutline';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { ViewControls } from '@/sections/Header/ViewControls/ViewControls';
import { UserMenuButton } from '@/components/shared/UserMenuButton/UserMenuButton';
import { SettingsPanel } from '@/components/Settings/SettingsPanel';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useTouchpoint } from '@/hooks/useTouchpoint';
import { isDesktop } from '@/consts/device';
import useStyles from './styles';

import type { SizeValue } from '@/components/shared/SizeSelector';

interface HeaderRightProps {
  isAuthorized: boolean | undefined;
  showViewControls: boolean;
  showGenerationsButton: boolean;
  viewCrop: boolean;
  viewSize: SizeValue;
  onLogin: () => void;
  onCropToggle: () => void;
  onSizeChange: (size: SizeValue) => void;
  onGenerationsClick: () => void;
  onCloseApp: () => void;
  onSettingsClick?: () => void;
}

export const HeaderRight = ({
  isAuthorized,
  showViewControls,
  showGenerationsButton,
  viewCrop,
  viewSize,
  onLogin,
  onCropToggle,
  onSizeChange,
  onGenerationsClick,
  onCloseApp,
  onSettingsClick,
}: HeaderRightProps) => {
  const classes = useStyles();
  const { showUserMenu } = useTouchpoint();
  const {
    isAuthorized: userIsAuthorized,
    username,
    avatarSrc,
    tierId,
  } = useUserProfile();
  const [showDesktopSettings, setShowDesktopSettings] = useState(false);

  const handleSettingsClick = () => {
    if (isDesktop) {
      setShowDesktopSettings((v) => !v);
    } else if (onSettingsClick) {
      onSettingsClick();
    }
  };

  return (
    <div className={classes.right}>
      {/* Login button - shown when not authorized and user menu is hidden */}
      {!isAuthorized && !showUserMenu && (
        <button
          type="button"
          className={classes.loginButton}
          onClick={onLogin}
          title="Log in"
          aria-label="Log in"
        >
          Log in
        </button>
      )}
      {/* View controls - mobile only, library screen, hidden when user menu is active */}
      {showViewControls && !showUserMenu && (
        <ViewControls
          viewCrop={viewCrop}
          viewSize={viewSize}
          onCropToggle={onCropToggle}
          onSizeChange={onSizeChange}
        />
      )}
      {/* Generations button - CSS hides on desktop */}
      {showGenerationsButton && (
        <button
          type="button"
          className={classes.generationsButton}
          onClick={onGenerationsClick}
          title="Your Creations"
          aria-label="Your Creations"
        >
          <IconImageOutline fill="currentColor" />
        </button>
      )}
      {/* User menu or close button */}
      {showUserMenu ? (
        <div className={classes.settingsWrapper}>
          <UserMenuButton
            isAuthorized={userIsAuthorized}
            username={username}
            avatarSrc={avatarSrc}
            tierId={tierId}
            showUserMenu={showUserMenu}
            onCloseApp={onCloseApp}
            onSettingsClick={handleSettingsClick}
          />
          {showDesktopSettings && isDesktop && (
            <SettingsPanel variant="dropdown" isAuthorized={userIsAuthorized} />
          )}
        </div>
      ) : (
        <button
          type="button"
          className={classes.closeButton}
          onClick={onCloseApp}
          title="Close"
          aria-label="Close app"
        >
          <IconCross fill="currentColor" width="20" height="20" />
        </button>
      )}
    </div>
  );
};
